import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, X, Plus, Minus, Trash2, Truck, CreditCard, FileImage, ExternalLink, Tag, CheckCircle, ChevronLeft, Package, DollarSign, Store } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import TermsModal from './TermsModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const CartDrawer = ({ buttonOnly = false, modalOnly = false }) => {
  const { t } = useTranslation();
  const { 
    cart, 
    itemCount, 
    loading, 
    removeFromCart, 
    updateQuantity,
    getShippingEstimates,
    selectShipping,
    createCheckoutSession,
    createCashCheckout,
    applyPromoCode,
    removePromoCode,
    isCartOpen,
    setIsCartOpen
  } = useCart();
  
  const [step, setStep] = useState('cart');
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: 'FL',
    zip_code: '',
    phone: '',
    country: 'US'
  });
  const [shippingEstimates, setShippingEstimates] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  
  // Terms and Conditions consent state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Payment method selection (additive): 'card' (Stripe, default) | 'cash-pickup' | 'cash-cod'
  const [paymentMethod, setPaymentMethod] = useState('card');
  // Cash order confirmation screen state
  const [cashOrderConfirmation, setCashOrderConfirmation] = useState(null);

  const handleAddressChange = (field, value) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleGetShippingEstimates = async () => {
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city || 
        !shippingAddress.state || !shippingAddress.zip_code) {
      alert(t('cart.fillAllFields'));
      return;
    }
    
    // Validate phone
    if (!shippingAddress.phone || shippingAddress.phone.length < 10) {
      setPhoneError(t('cart.phoneRequired') || 'Phone number is required');
      return;
    }
    setPhoneError('');

    setLoadingShipping(true);
    const estimates = await getShippingEstimates(shippingAddress);
    setShippingEstimates(estimates);
    
    // Auto-select first shipping option
    if (estimates?.estimates?.length > 0) {
      const firstOption = estimates.estimates[0];
      setSelectedShipping(firstOption);
      await selectShipping(firstOption.service_code, firstOption.price);
    }
    
    setLoadingShipping(false);
  };

  const handleSelectShipping = async (estimate) => {
    setSelectedShipping(estimate);
    await selectShipping(estimate.service_code, estimate.price);
  };

  // Promo code handlers
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    const result = await applyPromoCode(promoCode.trim());
    
    if (result.success) {
      setPromoCode('');
    } else {
      setPromoError(result.error);
    }
    
    setPromoLoading(false);
  };

  const handleRemovePromo = async () => {
    setPromoLoading(true);
    await removePromoCode();
    setPromoLoading(false);
  };

  const handleProceedToPayment = async () => {
    // Cash pickup does not require a shipping option (customer picks up in store).
    // Card and Cash-on-Delivery still require a shipping selection.
    if (paymentMethod !== 'cash-pickup' && !selectedShipping) {
      alert(t('cart.selectShipping'));
      return;
    }
    
    // Show terms modal instead of inline checkbox validation
    setShowTermsModal(true);
  };

  const handleTermsAccepted = async () => {
    setShowTermsModal(false);
    setTermsAccepted(true);
    setProcessingPayment(true);
    
    // Include terms consent data in checkout
    const termsConsentData = {
      terms_accepted: true,
      terms_accepted_at: new Date().toISOString(),
      customer_email: shippingAddress.email || null
    };

    // Cash payment branches — do NOT go to Stripe
    if (paymentMethod === 'cash-pickup' || paymentMethod === 'cash-cod') {
      const subtype = paymentMethod === 'cash-pickup' ? 'pickup' : 'cod';
      const result = await createCashCheckout(
        shippingAddress,
        selectedShipping?.service_name || (subtype === 'pickup' ? 'Recogida en Tienda' : 'Pago Contraentrega'),
        subtype,
        {
          name: shippingAddress.name,
          email: shippingAddress.email || null,
          phone: shippingAddress.phone,
        },
        termsConsentData,
      );
      if (result?.success) {
        setCashOrderConfirmation(result);
      } else {
        alert(result?.error || t('cart.paymentError'));
      }
      setProcessingPayment(false);
      return;
    }
    
    const session = await createCheckoutSession(shippingAddress, selectedShipping.service_name, termsConsentData);
    
    if (session?.checkout_url) {
      window.location.href = session.checkout_url;
    } else {
      alert(t('cart.paymentError'));
      setProcessingPayment(false);
    }
  };

  const handleClose = () => {
    setIsCartOpen(false);
    // Reset to cart step when closing
    setTimeout(() => setStep('cart'), 300);
  };

  // Cart Item Component - Enhanced with more details
  const CartItemCard = ({ item, index }) => (
    <div 
      className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
      data-testid={`cart-item-${index}`}
    >
      <div className="flex gap-4">
        {item.image_url && (
          <img 
            src={item.image_url} 
            alt={item.product_name}
            className="w-20 h-20 lg:w-24 lg:h-24 object-cover rounded-lg bg-slate-100"
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-slate-800 text-base lg:text-lg">{item.product_name}</h4>
          <div className="mt-1 space-y-0.5">
            <p className="text-sm text-slate-500">{t('cart.size')}: <span className="text-slate-700">{item.size}</span></p>
            {item.options?.quantityTier && (
              <p className="text-sm text-slate-500">{t('cart.quantity')}: <span className="text-slate-700">{item.options.quantityTier} units</span></p>
            )}
            {item.options?.stickerType && (
              <p className="text-sm text-slate-500">Type: <span className="text-slate-700">{item.options.stickerType}</span></p>
            )}
          </div>
          
          {/* Uploaded file indicator */}
          {item.uploaded_file && (
            <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
              <FileImage className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-xs text-green-700 truncate flex-1">{item.uploaded_file.filename}</span>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${item.uploaded_file.file_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 hover:bg-green-100 rounded transition-colors"
              >
                <ExternalLink className="w-3 h-3 text-green-600" />
              </a>
            </div>
          )}
        </div>
        
        <div className="flex flex-col items-end justify-between">
          <button 
            onClick={() => removeFromCart(index)}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            data-testid={`remove-item-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          
          <div className="text-right">
            <p className="text-lg lg:text-xl font-bold text-[#1A3A5C]">${item.unit_price.toFixed(2)}</p>
            {item.quantity > 1 && (
              <p className="text-xs text-slate-500">x{item.quantity} = ${(item.unit_price * item.quantity).toFixed(2)}</p>
            )}
          </div>
          
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button 
              onClick={() => updateQuantity(index, Math.max(1, item.quantity - 1))}
              className="w-7 h-7 flex items-center justify-center bg-white rounded hover:bg-slate-50 shadow-sm"
              data-testid={`decrease-qty-${index}`}
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
            <button 
              onClick={() => updateQuantity(index, item.quantity + 1)}
              className="w-7 h-7 flex items-center justify-center bg-white rounded hover:bg-slate-50 shadow-sm"
              data-testid={`increase-qty-${index}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Promo Code Section
  const PromoCodeSection = ({ isMobile = false }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`promo-code-section${isMobile ? '-mobile' : ''}`}>
      <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
        <Tag className="w-4 h-4 text-[#1A3A5C]" />
        {t('cart.promoCode') || 'Promo Code'}
      </Label>
      
      {cart?.promo_code ? (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <span className="font-semibold text-green-700">{cart.promo_code}</span>
              <span className="text-sm text-green-600 ml-2">
                -{cart.discount_percent}% (${cart.discount_amount?.toFixed(2)})
              </span>
            </div>
          </div>
          <button
            onClick={handleRemovePromo}
            disabled={promoLoading}
            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder={t('cart.enterPromoCode') || 'Enter code'}
            className="flex-1"
            data-testid={`promo-code-input${isMobile ? '-mobile' : ''}`}
          />
          <Button
            onClick={handleApplyPromo}
            disabled={promoLoading || !promoCode.trim()}
            className="bg-[#1A3A5C] hover:bg-[#152e4a]"
            data-testid={`apply-promo-btn${isMobile ? '-mobile' : ''}`}
          >
            {promoLoading ? '...' : (t('cart.apply') || 'Apply')}
          </Button>
        </div>
      )}
      
      {promoError && <p className="text-xs text-red-500 mt-2">{promoError}</p>}
    </div>
  );

  // Order Summary Component
  const OrderSummary = ({ showCheckoutButton = false, compact = false }) => (
    <div className={`bg-white rounded-xl border border-slate-200 ${compact ? 'p-3' : 'p-4 lg:p-6'}`} data-testid="cart-summary">
      <h3 className={`font-semibold text-slate-800 ${compact ? 'text-base mb-3' : 'text-lg mb-4'}`}>
        {t('cart.orderSummary') || 'Order Summary'}
      </h3>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">{t('cart.subtotal')}:</span>
          <span className="font-medium">${(cart?.subtotal || 0).toFixed(2)}</span>
        </div>
        
        {cart?.discount_amount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>{t('cart.discount') || 'Discount'} ({cart.promo_code}):</span>
            <span>-${cart.discount_amount.toFixed(2)}</span>
          </div>
        )}
        
        {cart?.handling_fee > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{t('cart.handlingFee') || 'Handling'} (3.5%):</span>
            <span className="font-medium">${cart.handling_fee.toFixed(2)}</span>
          </div>
        )}
        
        {selectedShipping && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{t('cart.shipping')}:</span>
            <span className={`font-medium ${cart?.shipping_cost === 0 ? 'text-green-600' : ''}`}>
              {cart?.shipping_cost === 0 ? `${t('cart.free')}!` : `$${(cart?.shipping_cost || 0).toFixed(2)}`}
            </span>
          </div>
        )}
        
        {cart?.sales_tax > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{t('cart.tax')} (7%):</span>
            <span className="font-medium">${cart.sales_tax.toFixed(2)}</span>
          </div>
        )}
        
        <div className="border-t border-slate-200 pt-3 mt-3">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-slate-800">{t('cart.total')}:</span>
            <span className="text-2xl font-bold text-[#1A3A5C]">
              ${(() => {
                // If cart.total is 0 but we have items, calculate a running total
                if (cart?.total > 0) return cart.total.toFixed(2);
                // Calculate running total: subtotal - discount + handling + shipping + tax
                let runningTotal = cart?.subtotal || 0;
                if (cart?.discount_amount > 0) runningTotal -= cart.discount_amount;
                if (cart?.handling_fee > 0) runningTotal += cart.handling_fee;
                if (cart?.shipping_cost > 0) runningTotal += cart.shipping_cost;
                if (cart?.sales_tax > 0) runningTotal += cart.sales_tax;
                return runningTotal.toFixed(2);
              })()}
            </span>
          </div>
        </div>
        
        {/* Free shipping message */}
        {cart?.subtotal < 250 && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              {t('cart.addMore')} <span className="font-semibold">${(250 - cart.subtotal).toFixed(2)}</span> {t('cart.forFreeShipping')}
            </p>
          </div>
        )}
        {cart?.subtotal >= 250 && step === 'cart' && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700 font-medium">{t('cart.qualifiesFree')}</p>
          </div>
        )}
      </div>
      
      {showCheckoutButton && (
        <div className="mt-4 space-y-3">
          {step === 'cart' && (
            <Button 
              onClick={() => setStep('shipping')}
              className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold"
              data-testid="proceed-to-shipping-btn"
            >
              <Truck className="w-5 h-5 mr-2" />
              {t('cart.proceedToCheckout')}
            </Button>
          )}
          
          {step === 'shipping' && (paymentMethod === 'cash-pickup' || selectedShipping) && (
            <>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-xs text-slate-600 text-center">
                  {t('cart.termsNote') || 'By clicking "Proceed to Payment", you will review and accept our Terms and Conditions'}
                </p>
              </div>
              <Button 
                onClick={handleProceedToPayment}
                disabled={(paymentMethod !== 'cash-pickup' && !selectedShipping) || processingPayment}
                className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold"
                data-testid="proceed-to-payment-btn"
              >
                {paymentMethod === 'card' ? <CreditCard className="w-5 h-5 mr-2" /> : <DollarSign className="w-5 h-5 mr-2" />}
                {processingPayment
                  ? t('cart.processing')
                  : paymentMethod === 'card'
                    ? (t('cart.proceedToPayment') || 'Proceder al Pago')
                    : (t('cart.confirmCashOrder') || 'Confirmar Orden (Efectivo)')
                }
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );

  // Shipping Form Component
  const ShippingForm = ({ isMobile = false }) => {
    const suffix = isMobile ? '-mobile' : '';
    return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 lg:p-6" data-testid={`shipping-form${suffix}`}>
      <h3 className="font-semibold text-slate-800 text-lg mb-4 flex items-center gap-2">
        <Truck className="w-5 h-5 text-[#1A3A5C]" />
        {t('cart.shippingInfo')}
      </h3>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor={`name${suffix}`} className="text-sm font-medium text-slate-700">{t('cart.fullName')}</Label>
          <Input
            id={`name${suffix}`}
            value={shippingAddress.name}
            onChange={(e) => handleAddressChange('name', e.target.value)}
            placeholder="John Doe"
            className="mt-1"
            data-testid={`shipping-name${suffix}`}
          />
        </div>
        
        <div>
          <Label htmlFor={`street${suffix}`} className="text-sm font-medium text-slate-700">{t('cart.streetAddress')}</Label>
          <Input
            id={`street${suffix}`}
            value={shippingAddress.street}
            onChange={(e) => handleAddressChange('street', e.target.value)}
            placeholder="123 Main St"
            className="mt-1"
            data-testid={`shipping-street${suffix}`}
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`city${suffix}`} className="text-sm font-medium text-slate-700">{t('cart.city')}</Label>
            <Input
              id={`city${suffix}`}
              value={shippingAddress.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              placeholder="Miami"
              className="mt-1"
              data-testid={`shipping-city${suffix}`}
            />
          </div>
          <div>
            <Label htmlFor={`state${suffix}`} className="text-sm font-medium text-slate-700">{t('cart.state')}</Label>
            <Select 
              value={shippingAddress.state} 
              onValueChange={(value) => handleAddressChange('state', value)}
            >
              <SelectTrigger className="mt-1" data-testid={`shipping-state${suffix}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(state => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.code} - {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`zip${suffix}`} className="text-sm font-medium text-slate-700">{t('cart.zipCode')}</Label>
            <Input
              id={`zip${suffix}`}
              value={shippingAddress.zip_code}
              onChange={(e) => handleAddressChange('zip_code', e.target.value)}
              placeholder="33101"
              maxLength={5}
              className="mt-1"
              data-testid={`shipping-zip${suffix}`}
            />
          </div>
          <div>
            <Label htmlFor={`phone${suffix}`} className="text-sm font-medium text-slate-700">
              {t('cart.phone') || 'Phone'} <span className="text-red-500">*</span>
            </Label>
            <Input
              id={`phone${suffix}`}
              type="tel"
              value={shippingAddress.phone}
              onChange={(e) => { handleAddressChange('phone', e.target.value); setPhoneError(''); }}
              placeholder="561-555-1234"
              className={`mt-1 ${phoneError ? 'border-red-500' : ''}`}
              data-testid={`shipping-phone${suffix}`}
            />
            {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
          </div>
        </div>

        <Button 
          onClick={handleGetShippingEstimates}
          disabled={loadingShipping}
          className="w-full h-11 bg-cyan-600 hover:bg-cyan-700"
          data-testid={`calculate-shipping-btn${suffix}`}
        >
          <Truck className="w-4 h-4 mr-2" />
          {loadingShipping ? t('cart.calculating') : t('cart.calculateShipping')}
        </Button>

        {shippingEstimates && (
          <div className="space-y-3 mt-4" data-testid={`shipping-options${suffix}`}>
            {shippingEstimates.qualifies_for_free_shipping && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 font-medium text-sm">{t('cart.congratsFree')}</p>
              </div>
            )}
            
            <h4 className="font-semibold text-sm text-slate-700">{t('cart.shippingOptions')}</h4>
            {shippingEstimates.estimates.map((estimate, idx) => (
              <div 
                key={idx}
                onClick={() => handleSelectShipping(estimate)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedShipping?.service_code === estimate.service_code
                    ? 'border-[#1A3A5C] bg-blue-50'
                    : 'border-slate-200 hover:border-[#1A3A5C]/50'
                }`}
                data-testid={`shipping-option-${idx}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-slate-800">{estimate.service_name}</p>
                    <p className="text-sm text-slate-500">{estimate.estimated_days}</p>
                  </div>
                  <p className={`text-lg font-bold ${estimate.price === 0 ? 'text-green-600' : 'text-[#1A3A5C]'}`}>
                    {estimate.price === 0 ? `${t('cart.free')}!` : `$${estimate.price.toFixed(2)}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    );
  };

  // Payment Method Selector — shown during shipping step.
  // Adds new options without removing the existing Stripe flow.
  const PaymentMethodSelector = ({ isMobile = false }) => {
    const suffix = isMobile ? '-mobile' : '';
    const options = [
      {
        key: 'card',
        icon: CreditCard,
        title: t('cart.paymentCard') || 'Tarjeta de Crédito/Débito',
        desc: t('cart.paymentCardDesc') || 'Procesamos con Stripe. Pago seguro online.',
      },
      {
        key: 'cash-pickup',
        icon: Store,
        title: t('cart.paymentPickup') || 'Pago en Efectivo (Recogida en Tienda)',
        desc: t('cart.paymentPickupDesc') || 'Sin costo de envío. Paga al recoger tu orden.',
      },
      {
        key: 'cash-cod',
        icon: DollarSign,
        title: t('cart.paymentCOD') || 'Pago en Efectivo (Contraentrega)',
        desc: t('cart.paymentCODDesc') || 'Paga en efectivo al recibir tu orden. Aplica costo de envío.',
      },
    ];
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 lg:p-6" data-testid={`payment-method-selector${suffix}`}>
        <h3 className="font-semibold text-slate-800 text-lg mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#1A3A5C]" />
          {t('cart.paymentMethod') || 'Método de Pago'}
        </h3>
        <div className="space-y-2">
          {options.map((opt) => {
            const Icon = opt.icon;
            const active = paymentMethod === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPaymentMethod(opt.key)}
                data-testid={`payment-option-${opt.key}${suffix}`}
                className={`w-full text-left p-3 border-2 rounded-lg transition-all flex gap-3 items-start ${
                  active
                    ? 'border-[#1A3A5C] bg-blue-50'
                    : 'border-slate-200 hover:border-[#1A3A5C]/50 bg-white'
                }`}
              >
                <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  active ? 'border-[#1A3A5C]' : 'border-slate-300'
                }`}>
                  {active && <div className="w-2.5 h-2.5 bg-[#1A3A5C] rounded-full" />}
                </div>
                <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${active ? 'text-[#1A3A5C]' : 'text-slate-500'}`} />
                <div className="flex-1">
                  <p className="font-semibold text-sm text-slate-800">{opt.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Cash Order Confirmation Screen — shown after a successful /api/checkout/cash call.
  const CashOrderConfirmation = () => {
    const result = cashOrderConfirmation;
    if (!result) return null;
    const subtype = result.payment_subtype;
    const instructions = subtype === 'pickup'
      ? (t('cart.cashPickupInstructions') || 'Tu orden quedó registrada. Te contactaremos al teléfono o correo provistos para coordinar la recogida en tienda y el pago en efectivo. No realices ningún pago todavía.')
      : (t('cart.cashCODInstructions') || 'Tu orden quedó registrada. Te contactaremos al teléfono o correo provistos para confirmar el envío. Pagarás en efectivo al recibir tu pedido.');
    const handleDone = () => {
      setCashOrderConfirmation(null);
      setIsCartOpen(false);
      // Reset step so the next open starts from the cart
      setTimeout(() => setStep('cart'), 300);
      // Force a refresh so the (now-converted) cart shows as empty
      if (typeof window !== 'undefined') window.location.reload();
    };
    return (
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50 overflow-y-auto" data-testid="cash-order-confirmation">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-1">
            {t('cart.cashOrderReceived') || '¡Orden registrada!'}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {t('cart.cashOrderReceivedSub') || 'Recibimos tu solicitud. No se ha procesado ningún cobro.'}
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 text-left space-y-2" data-testid="cash-order-summary">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t('cart.orderNumber') || 'Número de orden'}</span>
              <span className="font-bold text-[#1A3A5C]" data-testid="cash-order-number">#{result.order_number}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t('cart.paymentMethod') || 'Método de pago'}</span>
              <span className="font-medium text-slate-700">
                {subtype === 'pickup' ? (t('cart.paymentPickup') || 'Efectivo (Pickup)') : (t('cart.paymentCOD') || 'Efectivo (COD)')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t('cart.total') || 'Total'}</span>
              <span className="font-bold text-slate-800">${result.total?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t('cart.status') || 'Estado'}</span>
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                {t('cart.statusPendingCash') || 'Pago Pendiente en Efectivo'}
              </span>
            </div>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed mb-6">{instructions}</p>

          <Button
            onClick={handleDone}
            className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold"
            data-testid="cash-order-done-btn"
          >
            {t('cart.done') || 'Entendido'}
          </Button>
        </div>
      </div>
    );
  };

  // Empty Cart View
  const EmptyCart = () => (
    <div className="flex flex-col items-center justify-center h-full py-12">
      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
        <ShoppingCart className="w-12 h-12 text-slate-300" />
      </div>
      <h3 className="text-xl font-semibold text-slate-700 mb-2">{t('cart.empty')}</h3>
      <p className="text-slate-500 text-center mb-6">Start shopping to add items to your cart</p>
      <Button onClick={handleClose} variant="outline">
        Continue Shopping
      </Button>
    </div>
  );

  // If buttonOnly prop is true, only render the button (no modal)
  if (buttonOnly) {
    return (
      <button 
        onClick={() => setIsCartOpen(true)}
        className="relative p-2 text-gray-700 hover:text-cyan-600 transition-colors"
        data-testid="cart-button"
      >
        <ShoppingCart className="w-6 h-6" />
        {itemCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 text-white text-xs rounded-full flex items-center justify-center">
            {itemCount}
          </span>
        )}
      </button>
    );
  }

  // If modalOnly, don't render the button, just the modal
  if (modalOnly) {
    return (
      <>
        {/* Full Screen Cart Modal */}
        {isCartOpen && (
          <div 
            className="fixed inset-0 z-50"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={handleClose}
            />
            
            {/* Modal Container - Full screen on mobile, centered on desktop */}
            <div 
              className="absolute inset-0 lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[95%] lg:max-w-6xl lg:h-[90vh] lg:max-h-[900px] bg-slate-50 lg:rounded-2xl overflow-hidden shadow-2xl flex flex-col z-10"
              style={{ animation: 'slideUp 0.3s ease-out' }}
            >
              {/* Fixed Header */}
              <header className="bg-[#1A3A5C] text-white px-4 lg:px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  {step === 'shipping' && (
                    <button 
                      onClick={() => setStep('cart')}
                      className="p-1.5 hover:bg-white/10 rounded-lg transition-colors lg:hidden"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}
                  <div className="flex items-center gap-2">
                    <Package className="w-6 h-6" />
                    <span className="font-bold text-lg">ONE2TEES</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="hidden lg:block text-sm">
                    {step === 'cart' && `${itemCount} ${itemCount !== 1 ? t('cart.productsInCart') : t('cart.productInCart')}`}
                    {step === 'shipping' && t('cart.shippingInfo')}
                  </div>
                  <button 
                    onClick={handleClose}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </header>

              {/* Main Content Area */}
              <div className="flex-1 overflow-hidden flex flex-col bg-slate-50">
                {cashOrderConfirmation ? (
                  <CashOrderConfirmation />
                ) : (!cart?.items || cart.items.length === 0) ? (
                  <EmptyCart />
                ) : (
                  <>
                    {/* Mobile Layout */}
                    <div className="lg:hidden flex flex-col flex-1 overflow-hidden bg-slate-50">
                      <div className="flex-1 overflow-y-auto p-4 pb-36">
                        {step === 'cart' && (
                          <div className="space-y-4" data-testid="cart-items-mobile">
                            {cart.items.map((item, index) => (
                              <CartItemCard key={index} item={item} index={index} />
                            ))}
                            <PromoCodeSection isMobile={true} />
                            <OrderSummary />
                          </div>
                        )}
                        
                        {step === 'shipping' && (
                          <div className="space-y-4">
                            <ShippingForm isMobile={true} />
                            <PaymentMethodSelector isMobile={true} />
                            <PromoCodeSection isMobile={true} />
                          </div>
                        )}
                      </div>
                      
                      {/* Fixed Bottom Bar - Mobile */}
                      <div className="bg-white border-t border-slate-200 p-4 shadow-lg flex-shrink-0">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-slate-600">{t('cart.total')}:</span>
                          <span className="text-xl font-bold text-[#1A3A5C]">${(cart?.total || cart?.subtotal || 0).toFixed(2)}</span>
                        </div>
                        
                        {step === 'cart' && (
                          <Button 
                            onClick={() => setStep('shipping')}
                            className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold"
                            data-testid="proceed-to-shipping-btn-mobile"
                          >
                            <Truck className="w-5 h-5 mr-2" />
                            {t('cart.proceedToCheckout')}
                          </Button>
                        )}
                        
                        {step === 'shipping' && (
                          <Button 
                            onClick={handleProceedToPayment}
                            disabled={(paymentMethod !== 'cash-pickup' && !selectedShipping) || processingPayment}
                            className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold disabled:bg-slate-300"
                            data-testid="proceed-to-payment-btn-mobile"
                          >
                            {paymentMethod === 'card' ? <CreditCard className="w-5 h-5 mr-2" /> : <DollarSign className="w-5 h-5 mr-2" />}
                            {processingPayment
                              ? t('cart.processing')
                              : paymentMethod === 'card'
                                ? (t('cart.proceedToPayment') || 'Proceder al Pago')
                                : (t('cart.confirmCashOrder') || 'Confirmar Orden (Efectivo)')
                            }
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Desktop Layout - Two Columns */}
                    <div className="hidden lg:grid lg:grid-cols-5 gap-6 h-full p-6 overflow-hidden">
                      {/* Left Column - Cart Items / Shipping Form */}
                      <div className="col-span-3 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                        {step === 'cart' && (
                          <div className="space-y-4" data-testid="cart-items">
                            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                              <ShoppingCart className="w-6 h-6 text-[#1A3A5C]" />
                              {t('cart.title')} ({itemCount} {itemCount !== 1 ? 'items' : 'item'})
                            </h2>
                            {cart.items.map((item, index) => (
                              <CartItemCard key={index} item={item} index={index} />
                            ))}
                          </div>
                        )}
                        
                        {step === 'shipping' && (
                          <div className="space-y-4">
                            <button 
                              onClick={() => setStep('cart')}
                              className="flex items-center gap-2 text-[#1A3A5C] hover:underline mb-4"
                            >
                              <ChevronLeft className="w-4 h-4" />
                              {t('cart.backToCart')}
                            </button>
                            <ShippingForm />
                            <PaymentMethodSelector />
                          </div>
                        )}
                      </div>
                      
                      {/* Right Column - Order Summary */}
                      <div className="col-span-2 space-y-4">
                        <PromoCodeSection />
                        <OrderSummary showCheckoutButton={true} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Terms Modal */}
        <TermsModal 
          isOpen={showTermsModal}
          onClose={() => setShowTermsModal(false)}
          onAccept={handleTermsAccepted}
          orderTotal={cart?.total || cart?.subtotal}
        />

        {/* Animation Styles */}
        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      {/* Cart Button Trigger */}
      <button 
        onClick={() => setIsCartOpen(true)}
        className="relative p-2 text-gray-700 hover:text-cyan-600 transition-colors"
        data-testid="cart-button"
      >
        <ShoppingCart className="w-6 h-6" />
        {itemCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 text-white text-xs rounded-full flex items-center justify-center">
            {itemCount}
          </span>
        )}
      </button>

      {/* Full Screen Cart Modal */}
      {isCartOpen && (
        <div 
          className="fixed inset-0 z-50"
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Modal Container - Full screen on mobile, centered on desktop */}
          <div 
            className="absolute inset-0 lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[95%] lg:max-w-6xl lg:h-[90vh] lg:max-h-[900px] bg-slate-50 lg:rounded-2xl overflow-hidden shadow-2xl flex flex-col z-10"
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            {/* Fixed Header */}
            <header className="bg-[#1A3A5C] text-white px-4 lg:px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                {step === 'shipping' && (
                  <button 
                    onClick={() => setStep('cart')}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors lg:hidden"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="flex items-center gap-2">
                  <Package className="w-6 h-6" />
                  <span className="font-bold text-lg">ONE2TEES</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="hidden lg:block text-sm">
                  {step === 'cart' && `${itemCount} ${itemCount !== 1 ? t('cart.productsInCart') : t('cart.productInCart')}`}
                  {step === 'shipping' && t('cart.shippingInfo')}
                </div>
                <button 
                  onClick={handleClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>

            {/* Main Content Area */}
            <div className="flex-1 overflow-hidden flex flex-col bg-slate-50">
              {(!cart?.items || cart.items.length === 0) ? (
                <EmptyCart />
              ) : (
                <>
                  {/* Mobile Layout */}
                  <div className="lg:hidden flex flex-col flex-1 overflow-hidden bg-slate-50">
                    <div className="flex-1 overflow-y-auto p-4 pb-36">
                      {step === 'cart' && (
                        <div className="space-y-4" data-testid="cart-items-mobile">
                          {cart.items.map((item, index) => (
                            <CartItemCard key={index} item={item} index={index} />
                          ))}
                          <PromoCodeSection isMobile={true} />
                          <OrderSummary />
                        </div>
                      )}
                      
                      {step === 'shipping' && (
                        <div className="space-y-4">
                          <ShippingForm isMobile={true} />
                          <PromoCodeSection isMobile={true} />
                        </div>
                      )}
                    </div>
                    
                    {/* Fixed Bottom Bar - Mobile */}
                    <div className="bg-white border-t border-slate-200 p-4 shadow-lg flex-shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-slate-600">{t('cart.total')}:</span>
                        <span className="text-xl font-bold text-[#1A3A5C]">${(cart?.total || cart?.subtotal || 0).toFixed(2)}</span>
                      </div>
                      
                      {step === 'cart' && (
                        <Button 
                          onClick={() => setStep('shipping')}
                          className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold"
                          data-testid="proceed-to-shipping-btn-mobile"
                        >
                          <Truck className="w-5 h-5 mr-2" />
                          {t('cart.proceedToCheckout')}
                        </Button>
                      )}
                      
                      {step === 'shipping' && (
                        <Button 
                          onClick={handleProceedToPayment}
                          disabled={!selectedShipping || processingPayment}
                          className="w-full h-12 bg-[#1A3A5C] hover:bg-[#152e4a] text-base font-semibold disabled:bg-slate-300"
                          data-testid="proceed-to-payment-btn-mobile"
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          {processingPayment ? t('cart.processing') : t('cart.proceedToPayment') || 'Proceed to Payment'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Desktop Layout - Two Columns */}
                  <div className="hidden lg:grid lg:grid-cols-5 gap-6 h-full p-6 overflow-hidden">
                    {/* Left Column - Cart Items / Shipping Form */}
                    <div className="col-span-3 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                      {step === 'cart' && (
                        <div className="space-y-4" data-testid="cart-items">
                          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <ShoppingCart className="w-6 h-6 text-[#1A3A5C]" />
                            {t('cart.title')} ({itemCount} {itemCount !== 1 ? 'items' : 'item'})
                          </h2>
                          {cart.items.map((item, index) => (
                            <CartItemCard key={index} item={item} index={index} />
                          ))}
                        </div>
                      )}
                      
                      {step === 'shipping' && (
                        <div className="space-y-4">
                          <button 
                            onClick={() => setStep('cart')}
                            className="flex items-center gap-2 text-[#1A3A5C] hover:underline mb-4"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            {t('cart.backToCart')}
                          </button>
                          <ShippingForm />
                        </div>
                      )}
                    </div>
                    
                    {/* Right Column - Order Summary */}
                    <div className="col-span-2 space-y-4">
                      <PromoCodeSection />
                      <OrderSummary showCheckoutButton={true} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      <TermsModal 
        isCartOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccepted}
        orderTotal={cart?.total || cart?.subtotal}
      />

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default CartDrawer;
